import type { LocationQuery } from 'vue-router';
import { getApiEndpoint } from '~/api/auth';
import type { ActionBlock, EntityType, PayItem, Project, Service, Suggestion } from '~/api/types.ts';
import { authFetch } from '~/api/utils';
import * as Sentry from '@sentry/vue';
import type { NuxtError } from '#app';

export interface GenerateProjectApiError {
  message: string;
  errors: { prompt: Array<string>; level: Array<string> };
  status: number;
}

export const generateProjectApi = async (prompt: string, level: string, entity: string = 'project'): Promise<Project> => {
  try {
    return await authFetch<Project>(getApiEndpoint() + 'projects/', {
      method: 'POST',
      body: {
        prompt,
        level,
        entity,
      },
    });
  } catch (err: unknown) {
    const error = {
      ...((err as NuxtError).data as GenerateProjectApiError),
      status: (err as NuxtError).statusCode,
    };

    Sentry.captureException(err);
    throw error;
  }
};

export const getProjects = async (entity: string = 'project'): Promise<Project[]> => {
  return await authFetch<Project[]>(getApiEndpoint() + 'projects/', {
    method: 'GET',
    params: { entity },
  });
};

export const getProject = async (id: string, query?: LocationQuery): Promise<Project | undefined> => {
  try {
    return await authFetch(getApiEndpoint() + 'projects/' + id, {
      method: 'GET',
      params: query,
    });
  } catch (err) {
    Sentry.captureException(err);
  }
};

export const rateProject = async (id: string | number, rating: number, comment?: string): Promise<Project> => {
  return await authFetch(getApiEndpoint() + 'projects/' + id + '/rate', {
    method: 'POST',
    body: {
      rating,
      rating_comment: comment,
    },
  });
};

export const logView = async (
  event: string,
  projectId: string | null,
  yandexClientId: string | null,
  fingerprint: string | null
): Promise<Project> => {
  return await authFetch(getApiEndpoint() + 'log/', {
    method: 'POST',
    body: {
      event,
      project_id: projectId,
      yandexClientId,
      fingerprint,
    },
  });
};

export const getSimilarProject = async (id: string): Promise<Project[]> => {
  return await authFetch(getApiEndpoint() + 'projects/' + id + '/similar', {
    method: 'GET',
  });
};

export const getOwnProject = async (id: string) => {
  return await authFetch(getApiEndpoint() + 'projects/' + id, {
    method: 'GET',
  });
};

export const copyProjectById = async (id: string): Promise<Project> => {
  return await authFetch(getApiEndpoint() + 'projects/' + id + '/copy', {
    method: 'POST',
  });
};

export type PaymentType = 'ru' | 'en' | 'sbp';

export const getPaymentLink = async (
  id: string,
  services: string[],
  paymentType: PaymentType,
  promocode?: string,
  bonus?: number
): Promise<{ payment_link: string; amount: number; items: PayItem[] }> => {
  return await authFetch(getApiEndpoint() + 'projects/' + id + '/payment_link', {
    method: 'POST',
    body: {
      services,
      payment_type: paymentType,
      promocode,
      bonus,
    },
  });
};

export const getActionBlock = async (props: { bonus?: number; promocode?: string; projectId: number; services: Service[] }) => {
  const url = getApiEndpoint() + `projects/${props.projectId}/cart`;

  return await authFetch<ActionBlock>(url, {
    method: 'GET',
    params: {
      bonus: props.bonus,
      promocode: props.promocode,
      'services[]': props.services,
    },
  });
};

export const getAverageTime = async () => {
  const data = await authFetch<{ result: number }>(getApiEndpoint() + 'job_time_average', { method: 'GET' });
  return data;
};

export const getSuggestions = async (query: string, entity: EntityType) => {
  return await authFetch<Suggestion[]>(getApiEndpoint() + 'suggest', {
    method: 'GET',
    params: { query, entity },
  });
};

export type ProjectGenerateStatus = {
  project: Project;
  status: 'pending' | 'success' | 'error';
  now: string;
  last_update_time: string;
};

export const getGenerates = async () => {
  try {
    const data = await authFetch<{ data: ProjectGenerateStatus[] }>(getApiEndpoint() + 'generates', { method: 'GET' });
    return data.data;
  } catch (err) {
    return undefined;
  }
};
